<template>
  <div v-if="data.length > 1" class="mx-20 flex flex-col items-center justify-center">
    <div class="pe-progress my-10">
      <div ref="progress" class="progress"></div>
      <div
        v-for="(step, key) in data"
        :key="key"
        class="circle"
        :class="{
          'pe-progress--complete': currentStep > key + 1,
          'pe-progress--active': key + 1 === currentStep,
          'cursor-pointer': navigable,
          'pointer-events-none': !navigable
        }"
        @click="emit('update:modelValue', key + 1)"
      >
        <span
          class="absolute top-[-30px] transform whitespace-nowrap uppercase"
          :class="{
            'font-bold text-black dark:text-neutral-200': key + 1 === currentStep,
            ' dark:text-neutral-500': key + 1 !== currentStep,
          }"
          >{{ step.label || `Passo ${key + 1}` }}</span
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { clamp } from '@/common/util'

const props = defineProps<{
  modelValue: number
  data: {
    label?: string
    navigable?: boolean
  }[]
  step?: number
  navigable?: boolean
}>()
const emit = defineEmits(['update:modelValue'])

const progress = ref()
const currentStep = computed(() => clamp(props.modelValue, 1, props.data.length))

const progressPercent = computed(() => {
  if (props.data.length < 2) return '0%'
  return `${((currentStep.value - 1) * 100) / (props.data.length - 1)}%`
})
</script>

<style lang="scss" scoped>
.pe-progress::before {
  content: '';
  background: #e0e0e0;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  height: 4px;
  width: 100%;
  z-index: -1;
}

.pe-progress {
  @apply relative z-[1] flex w-full max-w-full items-center justify-between;
}

.progress {
  @apply absolute top-1/2 left-0 z-[-1] h-1 bg-emerald-400 transition-all duration-300;
  width: v-bind(progressPercent);
  transform: translateY(-50%);
}

.circle {
  @apply relative flex h-[24px] w-[24px] items-center justify-center rounded-full border-[3px] border-solid border-neutral-300 bg-white text-neutral-400 transition-all duration-300;

  &::after {
    content: '';
    width: 8px;
    height: 20px;
    border-right: 2px solid #000;
    border-bottom: 2px solid #000;
    transform-origin: 50% 50%;
    @apply origin-bottom-right -translate-x-[5px] -translate-y-2 rotate-45 scale-75 opacity-0;
    transition: all ease-in-out 0.3s;
  }
}

.pe-progress--complete {
  @apply border-0 border-transparent bg-emerald-400;
  position: relative;

  &::after {
    @apply -translate-x-[5px] -translate-y-2 rotate-45 scale-100 opacity-100;
  }
}
.pe-progress--active {
  @apply border-emerald-400;
}
</style>
